import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useIndividualEntries,
  useCreateIndividualEntry,
  useUpdateIndividualEntry,
  useDeleteIndividualEntry,
} from '../../../api/hooks/useIndividualEntries';
import type {
  CreateIndividualEntryRequest,
  IndividualEntry,
} from '../../../types/individualEntry';
import { IndividualEntryDialog } from '../../../components/individuals/IndividualEntryDialog';
import { IndividualEntriesTable } from '../../../components/individuals/IndividualEntriesTable';
import { DrawStartNumbersButton } from '../../../components/individuals/DrawStartNumbersButton';
import { ConfirmDialog } from '../../../components/ConfirmDialog';

interface Props {
  categoryId: number;
}

export function IndividualEntriesTab({ categoryId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<IndividualEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<IndividualEntry | null>(null);

  const entriesQuery = useIndividualEntries(categoryId);
  const createMut = useCreateIndividualEntry(categoryId);
  const updateMut = useUpdateIndividualEntry(categoryId);
  const deleteMut = useDeleteIndividualEntry(categoryId);

  const entries = entriesQuery.data ?? [];

  const openCreate = () => {
    setEditingEntry(null);
    setDialogOpen(true);
  };

  const openEdit = (entry: IndividualEntry) => {
    setEditingEntry(entry);
    setDialogOpen(true);
  };

  const handleSubmit = (req: CreateIndividualEntryRequest) => {
    if (editingEntry) {
      updateMut.mutate(
        { id: editingEntry.id, req },
        {
          onSuccess: () => {
            enqueueSnackbar('Jednotlivec upraven', { variant: 'success' });
            setDialogOpen(false);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (e: any) =>
            enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
        },
      );
    } else {
      createMut.mutate(req, {
        onSuccess: () => {
          enqueueSnackbar('Jednotlivec vytvořen', { variant: 'success' });
          setDialogOpen(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (e: any) =>
          enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteMut.mutate(confirmDelete.id, {
      onSuccess: () => {
        enqueueSnackbar('Jednotlivec smazán', { variant: 'success' });
        setConfirmDelete(null);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (e: any) =>
        enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          Jednotlivci
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <DrawStartNumbersButton
          categoryId={categoryId}
          disabled={entries.length === 0}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Přidat jednotlivce
        </Button>
      </Stack>

      <IndividualEntriesTable
        entries={entries}
        loading={entriesQuery.isLoading}
        onEdit={openEdit}
        onDelete={(e) => setConfirmDelete(e)}
      />

      <IndividualEntryDialog
        open={dialogOpen}
        entry={editingEntry}
        loading={createMut.isPending || updateMut.isPending}
        onCancel={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        title="Smazat jednotlivce?"
        message={
          confirmDelete
            ? `Smazat ${confirmDelete.lastName} ${confirmDelete.firstName}?`
            : ''
        }
        destructive
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
